import {inject, Injectable, InjectionToken} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from '@services/config/config.service';

export const RESOURCE_TOKEN = new InjectionToken<string>('RESOURCE');
export const API_KEY_HEADER_TOKEN = new InjectionToken<boolean>('API_KEY_HEADER');

@Injectable({
    providedIn: 'root'
})
export abstract class DataService {

    protected readonly http = inject(HttpClient);
    protected readonly configService = inject(ConfigService);

    protected readonly resource = inject(RESOURCE_TOKEN, { optional: true });
    protected readonly apikeyEnabled = inject(API_KEY_HEADER_TOKEN, { optional: true }) ?? true;

    private _apiKey = '';

    abstract get url(): string;

    get ApiKey() {
        return this._apiKey;
    }

    set ApiKey(apiKey) {
        this._apiKey = apiKey;
    }

    private static createHeaders(apiKey = '', apiKeyEnabled: boolean) {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        if (apiKeyEnabled && apiKey) {
            headers = headers.append('apikey', apiKey);
        }
        return headers;
    }

    getAll<T>(resource = '', params: Record<string, string | boolean> | null = null, disableApiKey = false): Observable<T> {
        const headers = DataService.createHeaders(this.ApiKey, disableApiKey ? false : this.apikeyEnabled);
        let url = this.url;
        if (resource) {
            url = `${url}${resource}`;
        }
        // @ts-expect-error type mismatch with params
        return this.http.get<T>(url, {headers: headers, params: params});
    }

    get<T>(id: string, params: Record<string, string | boolean> | null = null): Observable<T> {
        const headers = DataService.createHeaders(this.ApiKey, this.apikeyEnabled);

        let url = `${this.url}/${id}`;
        if (params) {
            url = `${url}/${params}`;
        }
        return this.http.get<T>(url, {headers: headers});
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    create<T>(request: any, resource?: string): Observable<T> {
        const headers = DataService.createHeaders(this.ApiKey, this.apikeyEnabled);

        let url = this.url;
        if (resource) {
            url = `${url}/${resource}`;
        }
        return this.http.post<T>(url, request, {headers: headers});
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    update<T>(request: any, resource?: string, identifier = 'id'): Observable<T> {
        const headers = DataService.createHeaders(this.ApiKey, this.apikeyEnabled);
        let url = this.url;
        if (request[identifier]) {
            url = `${url}/${request[identifier]}`;
        }
        if (resource) {
            url = `${url}/${resource}`;
        }
        return this.http.put<T>(url, request, {headers: headers});
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete(request: any, resource?: string) {
        const headers = DataService.createHeaders(this.ApiKey, this.apikeyEnabled);
        let url = this.url;
        if (resource) {
            url = `${url}/${resource}`;
        }
        return this.http.delete(url, { headers: headers, body: request });
    }
}
